<template>
    <section class="live2d-panel">
        <div class="live2d-panel__header">
            <div>
                <h3>Live2D 数字人预览</h3>
                <p>使用微软 TTS 返回的视素数据驱动本地 Live2D 模型说话。</p>
            </div>
            <div class="live2d-panel__meta">
                <span v-if="trackInfo.visemeCount">视素 {{ trackInfo.visemeCount }} 条</span>
                <span v-if="currentVisemeId || isAudioPlaying">当前 {{ currentVisemeId }}</span>
            </div>
        </div>

        <div class="live2d-stage-wrap">
            <div ref="stage" class="live2d-stage"></div>
            <div v-if="runtimeError" class="live2d-stage__overlay live2d-stage__overlay--error">
                {{ runtimeError }}
            </div>
            <div v-else-if="!modelReady" class="live2d-stage__overlay">
                正在加载 Live2D 模型...
            </div>
        </div>

        <div class="live2d-panel__footer">
            <div class="live2d-status">{{ statusText }}</div>
            <div class="live2d-note">
                当前为测试体验版本，如您有进一步完善或新增功能的需求，欢迎联系我们继续定制。
            </div>
        </div>
    </section>
</template>

<script>
let live2dRuntimePromise = null

function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value))
}

function lerp(from, to, factor) {
    return from + (to - from) * factor
}

const LIVE2D_VISUAL_OFFSET_X = 142
const LIVE2D_VISUAL_OFFSET_Y = 30

function emptyTrack() {
    return {
        key: '',
        text: '',
        visemes: [],
        audioDurationMs: 0,
        visemeCount: 0
    }
}

export default {
    name: 'AzureTtsLive2dPanel',
    data() {
        return {
            pixiApp: null,
            live2dModel: null,
            resizeHandler: null,
            pointerMoveHandler: null,
            tickerHandler: null,
            stageRectCache: null,
            audioElement: null,
            removeAudioListeners: null,
            modelReady: false,
            runtimeError: '',
            statusText: '绛夊緟鐢熸垚闊抽',
            trackInfo: emptyTrack(),
            isAudioPlaying: false,
            currentVisemeId: 0,
            targetOpen: 0,
            targetForm: 0,
            currentOpen: 0,
            currentForm: 0,
            lastCueIndex: 0,
            mouthConfig: null,
            gestureConfig: null,
            idleClockMs: 0,
            isDestroying: false,
            initRequestId: 0,
            speechCues: [],
            speechCueIndex: 0,
            speechAccent: 0,
            speechHeadLift: 0,
            speechBodyTilt: 0,
            speechFormBias: 0,
            speechGazeBiasX: 0,
            speechEnergy: 0,
            blinkWaitMs: 1600,
            blinkElapsedMs: -1,
            blinkOpen: 1,
            armLeadSide: 1,
            armUpperL: 0,
            armUpperR: 0,
            armLowerL: 0,
            armLowerR: 0,
            armUpperLTarget: 0,
            armUpperRTarget: 0,
            armLowerLTarget: 0,
            armLowerRTarget: 0,
            speechMood: {
                headLift: 0,
                bodyTilt: 0,
                formBias: 0
            },
            debugState: {
                targetOpen: '0.000',
                currentOpen: '0.000',
                modelOpen: '0.000',
                rawOpen: '0.000',
                audioAttached: false,
                audioPlaying: false,
                lipSyncEnabled: false,
                mouthOpenId: '',
                rawCandidates: '',
                lastActiveSummary: ''
            },
            modelMetrics: {
                width: 1,
                height: 1
            }
        }
    },
    mounted() {
        this.initLive2d()
    },
    beforeDestroy() {
        this.isDestroying = true
        this.destroyLive2d()
        this.detachAudioElement()
    },
    methods: {
        getLive2dRuntimeUrl() {
            return this.getBaseUrl() + '/live2d-runtime/l2d.umd.js'
        },
        async loadLive2dRuntime() {
            if (!live2dRuntimePromise) {
                live2dRuntimePromise = new Promise((resolve, reject) => {
                    if (window.l2d && window.l2d.initializeLive2D && window.l2d.Live2DModel && window.l2d.PIXI) {
                        resolve(window.l2d)
                        return
                    }
                    const existing = document.querySelector('script[data-live2d-runtime="true"]')
                    if (existing) {
                        existing.addEventListener('load', () => {
                            if (window.l2d) resolve(window.l2d)
                            else reject(new Error('Live2D runtime global missing after script load'))
                        }, { once: true })
                        existing.addEventListener('error', () => reject(new Error('Live2D runtime script load failed')), { once: true })
                        return
                    }
                    const script = document.createElement('script')
                    script.src = this.getLive2dRuntimeUrl()
                    script.async = true
                    script.defer = true
                    script.dataset.live2dRuntime = 'true'
                    script.onload = () => {
                        if (window.l2d && window.l2d.initializeLive2D && window.l2d.Live2DModel && window.l2d.PIXI) {
                            resolve(window.l2d)
                            return
                        }
                        reject(new Error('Live2D runtime global missing after script load'))
                    }
                    script.onerror = () => reject(new Error('Live2D runtime script load failed'))
                    document.head.appendChild(script)
                })
            }
            return await live2dRuntimePromise
        },
        getBaseUrl() {
            return String((this.$store && this.$store.state && this.$store.state.basicurl) || '').replace(/\/+$/, '')
        },
        getModelUrl() {
            return this.getBaseUrl() + '/live2d-models/haru/runtime/haru.model3.json'
        },
        getModelMocUrl() {
            return this.getBaseUrl() + '/live2d-models/haru/runtime/haru.moc3'
        },
        async readModelMocVersion() {
            const response = await fetch(this.getModelMocUrl(), {
                method: 'GET',
                cache: 'no-store'
            })
            if (!response.ok) {
                throw new Error('Failed to read Live2D model file: ' + response.status)
            }
            const buffer = await response.arrayBuffer()
            const bytes = new Uint8Array(buffer)
            if (bytes.length < 8) {
                throw new Error('Invalid Live2D model file: moc3 data is too short')
            }
            const signature = String.fromCharCode(bytes[0], bytes[1], bytes[2], bytes[3])
            if (signature !== 'MOC3') {
                throw new Error('Invalid Live2D model file: MOC3 signature not found')
            }
            return Number(bytes[4]) || 0
        },
        async initLive2d() {
            if (this.pixiApp || !this.$refs.stage) return
            const requestId = ++this.initRequestId
            this.runtimeError = ''
            this.modelReady = false
            try {
                const mocVersion = await this.readModelMocVersion()
                if (this.isDestroying || requestId !== this.initRequestId) return
                if (mocVersion > 5) {
                    throw new Error(
                        'Current Live2D model uses MOC3 v' + mocVersion + ', but the runtime only supports up to v5. Please switch to an older exported model.'
                    )
                }
                const runtime = await this.loadLive2dRuntime()
                if (this.isDestroying || requestId !== this.initRequestId || !this.$refs.stage) return
                const initializeLive2D = runtime && runtime.initializeLive2D
                const Live2DModel = runtime && runtime.Live2DModel
                const PIXI = runtime && runtime.PIXI
                if (!initializeLive2D || !Live2DModel || !PIXI) {
                    throw new Error('Live2D runtime exports missing')
                }
                await initializeLive2D()
                window.PIXI = PIXI

                const stage = this.$refs.stage
                const width = Math.max(stage.clientWidth || 0, 360)
                const height = Math.max(stage.clientHeight || 0, 420)
                const app = new PIXI.Application({
                    width,
                    height,
                    antialias: true,
                    autoStart: true,
                    autoDensity: true,
                    backgroundAlpha: 0,
                    resolution: window.devicePixelRatio || 1
                })

                app.view.style.width = '100%'
                app.view.style.height = '100%'
                app.view.style.display = 'block'
                if (this.isDestroying || requestId !== this.initRequestId || !this.$refs.stage) {
                    app.destroy(true, { children: true, texture: false, baseTexture: false })
                    return
                }
                stage.innerHTML = ''
                stage.appendChild(app.view)

                const model = await Live2DModel.from(this.getModelUrl(), {
                    ticker: app.ticker,
                    autoInteract: false
                })
                if (this.isDestroying || requestId !== this.initRequestId || !this.$refs.stage) {
                    try {
                        if (app.stage && typeof app.stage.removeChild === 'function' && model) {
                            app.stage.removeChild(model)
                        }
                    } catch (e) {}
                    try {
                        if (model && typeof model.destroy === 'function') {
                            model.destroy({ children: true })
                        }
                    } catch (e) {}
                    app.destroy(true, { children: true, texture: false, baseTexture: false })
                    return
                }

                this.patchInternalModelRuntime(model)
                app.stage.addChild(model)

                this.pixiApp = app
                this.live2dModel = model
                this.modelMetrics = {
                    width: Math.max(model.width || 1, 1),
                    height: Math.max(model.height || 1, 1)
                }
                model.visible = true
                model.alpha = 1

                this.syncMouthConfig()
                this.syncGestureConfig()
                this.fitModel()
                this.bindPointerTracking()

                this.tickerHandler = delta => {
                    this.handleTicker(delta)
                }
                app.ticker.add(this.tickerHandler)

                this.resizeHandler = () => {
                    this.stageRectCache = null
                    this.fitModel()
                }
                window.addEventListener('resize', this.resizeHandler)

                this.modelReady = true
                this.startDefaultMotion()
                this.statusText = 'Model ready, waiting for audio playback'
            } catch (error) {
                if (this.isDestroying || requestId !== this.initRequestId) return
                this.runtimeError = (error && error.message) || 'Failed to load Live2D model. Please confirm local assets and service are available.'
                this.statusText = 'Live2D initialization failed'
                console.error('[AzureTtsLive2dPanel] init failed:', error)
            }
        },
        patchInternalModelRuntime(model) {
            const internalModel = model && model.internalModel
            if (!internalModel) return
            if (!internalModel.__webglGuardPatched && typeof internalModel.updateWebGLContext === 'function') {
                internalModel.updateWebGLContext = (...args) => {
                    const renderer = internalModel.renderer
                    if (!renderer || typeof renderer.startUp !== 'function') return
                    renderer.firstDraw = true
                    renderer._bufferData = {
                        vertex: null,
                        uv: null,
                        index: null
                    }
                    renderer.startUp(args[0])
                    if (renderer._clippingManager) {
                        renderer._clippingManager._currentFrameNo = args[1]
                        renderer._clippingManager._maskTexture = undefined
                    }
                    const shader = window.l2d &&
                        window.l2d.PIXI &&
                        window.l2d.PIXI.live2d &&
                        window.l2d.PIXI.live2d.Cubism4Shader
                    if (shader && Array.isArray(shader._shaderSets)) {
                        shader._shaderSets = []
                    }
                }
                internalModel.__webglGuardPatched = true
            }
            const motionManager = internalModel.motionManager
            if (motionManager && !motionManager.__visemeLipSyncPatched) {
                motionManager.mouthSync = () => clamp(this.currentOpen, 0, 1)
                motionManager.__visemeLipSyncPatched = true
            }
            if (!internalModel.__visemeUpdatePatched && typeof internalModel.update === 'function') {
                const rawUpdate = internalModel.update.bind(internalModel)
                internalModel.update = (...args) => {
                    const result = rawUpdate(...args)
                    this.applyGestureParams(true)
                    this.applyMouthParams(true)
                    if (this.mouthConfig && this.mouthConfig.coreModel &&
                        typeof this.mouthConfig.coreModel.update === 'function') {
                        this.mouthConfig.coreModel.update()
                    }
                    this.refreshDebugState()
                    return result
                }
                internalModel.__visemeUpdatePatched = true
            }
            if (!internalModel.__visemeDrawPatched && typeof internalModel.draw === 'function') {
                const rawDraw = internalModel.draw.bind(internalModel)
                internalModel.draw = (...args) => {
                    this.applyGestureParams(true)
                    this.applyMouthParams(true)
                    return rawDraw(...args)
                }
                internalModel.__visemeDrawPatched = true
            }
            if (!internalModel.__visemeMouthParamPatched && typeof internalModel.on === 'function') {
                internalModel.on('beforeModelUpdate', () => {
                    this.applyMouthParams()
                })
                internalModel.__visemeMouthParamPatched = true
            }
            this.syncRuntimeLipSyncState()
        },
        syncRuntimeLipSyncState() {
            const internalModel = this.live2dModel &&
                this.live2dModel.internalModel
            const motionManager = internalModel &&
                internalModel.motionManager
            if (!motionManager) return
            if (internalModel && Object.prototype.hasOwnProperty.call(internalModel, 'lipSync')) {
                internalModel.lipSync = !!(this.trackInfo.visemeCount && this.mouthConfig && this.mouthConfig.mouthOpenIndex >= 0)
            }
            motionManager.currentAudio = this.audioElement && this.isAudioPlaying && this.trackInfo.visemeCount
                ? this.audioElement
                : null
            this.refreshDebugState()
        },
        destroyLive2d() {
            this.initRequestId += 1
            if (this.pointerMoveHandler && this.$refs.stage) {
                this.$refs.stage.removeEventListener('pointermove', this.pointerMoveHandler)
                this.pointerMoveHandler = null
            }
            if (this.resizeHandler) {
                window.removeEventListener('resize', this.resizeHandler)
                this.resizeHandler = null
            }
            const app = this.pixiApp
            const tickerHandler = this.tickerHandler
            this.tickerHandler = null
            this.pixiApp = null
            if (app) {
                if (tickerHandler && app.ticker && typeof app.ticker.remove === 'function') {
                    try {
                        app.ticker.remove(tickerHandler)
                    } catch (error) {
                        console.warn('[AzureTtsLive2dPanel] ticker remove failed during destroy:', error)
                    }
                }
                try {
                    app.destroy(true, { children: true, texture: false, baseTexture: false })
                } catch (error) {
                    console.warn('[AzureTtsLive2dPanel] pixi destroy failed:', error)
                }
            }
            this.live2dModel = null
            this.mouthConfig = null
            this.gestureConfig = null
            this.stageRectCache = null
        },
        bindPointerTracking() {
            if (!this.$refs.stage || !this.live2dModel || this.pointerMoveHandler) return
            this.pointerMoveHandler = event => {
                if (!this.live2dModel || !this.live2dModel.internalModel) return
                const rect = this.stageRectCache || this.$refs.stage.getBoundingClientRect()
                this.stageRectCache = rect
                if (!rect.width || !rect.height) return
                const centerX = rect.left + rect.width / 2
                const centerY = rect.top + rect.height / 2
                const focusX = clamp((event.clientX - centerX) / rect.width, -1, 1)
                const focusY = clamp(-(event.clientY - centerY) / rect.height, -1, 1)
                this.live2dModel.internalModel.focusController &&
                    this.live2dModel.internalModel.focusController.focus(focusX, focusY)
            }
            this.$refs.stage.addEventListener('pointermove', this.pointerMoveHandler, { passive: true })
        },
        async startDefaultMotion() {
            if (!this.live2dModel || !this.live2dModel.internalModel) return
            const definitions = (this.live2dModel.internalModel.motionManager && this.live2dModel.internalModel.motionManager.definitions) || {}
            const groups = Object.keys(definitions || {})
            const idleGroup = groups.find(name => String(name).toLowerCase() === 'idle') || groups[0]
            if (!idleGroup) return
            try {
                await this.live2dModel.motion(idleGroup)
            } catch (error) {
                console.warn('[AzureTtsLive2dPanel] start motion failed:', error)
            }
        },
        fitModel() {
            if (!this.pixiApp || !this.live2dModel || !this.$refs.stage) return
            const stage = this.$refs.stage
            const width = Math.max(stage.clientWidth || 0, 360)
            const height = Math.max(stage.clientHeight || 0, 420)
            this.pixiApp.renderer.resize(width, height)
            const layoutBounds = typeof this.live2dModel.getLocalBounds === 'function'
                ? this.live2dModel.getLocalBounds()
                : { x: 0, y: 0, width: this.modelMetrics.width, height: this.modelMetrics.height }
            const safeBoundsWidth = Math.max(layoutBounds.width || this.modelMetrics.width, 1)
            const safeBoundsHeight = Math.max(layoutBounds.height || this.modelMetrics.height, 1)
            const stagePaddingX = 18
            const stagePaddingY = 18
            const scale = Math.min(
                (width - stagePaddingX * 2) / safeBoundsWidth,
                (height - stagePaddingY * 2) / safeBoundsHeight
            )
            this.live2dModel.scale.set(scale)
            const pivotX = layoutBounds.x + safeBoundsWidth / 2
            const pivotY = layoutBounds.y + safeBoundsHeight / 2
            if (this.live2dModel.pivot && typeof this.live2dModel.pivot.set === 'function') {
                this.live2dModel.pivot.set(pivotX, pivotY)
            }
            this.live2dModel.position.set(width / 2, height / 2)
            if (typeof this.live2dModel.getBounds === 'function') {
                const actualBounds = this.live2dModel.getBounds()
                const actualCenterX = actualBounds.x + actualBounds.width / 2
                const actualCenterY = actualBounds.y + actualBounds.height / 2
                this.live2dModel.position.set(
                    this.live2dModel.position.x + (width / 2 - actualCenterX) + LIVE2D_VISUAL_OFFSET_X,
                    this.live2dModel.position.y + (height / 2 - actualCenterY) + LIVE2D_VISUAL_OFFSET_Y
                )
                const adjustedBounds = this.live2dModel.getBounds()
                const overflowLeft = stagePaddingX - adjustedBounds.x
                const overflowRight = adjustedBounds.x + adjustedBounds.width - (width - stagePaddingX)
                const overflowTop = stagePaddingY - adjustedBounds.y
                const overflowBottom = adjustedBounds.y + adjustedBounds.height - (height - stagePaddingY)
                let shiftX = 0
                let shiftY = 0
                if (overflowLeft > 0) shiftX += overflowLeft
                if (overflowRight > 0) shiftX -= overflowRight
                if (overflowTop > 0) shiftY += overflowTop
                if (overflowBottom > 0) shiftY -= overflowBottom
                if (shiftX || shiftY) {
                    this.live2dModel.position.set(
                        this.live2dModel.position.x + shiftX,
                        this.live2dModel.position.y + shiftY
                    )
                }
            }
        },
        attachAudioElement(element) {
            if (!element || this.audioElement === element) return
            this.detachAudioElement()
            this.audioElement = element
            const onPlay = () => {
                this.isAudioPlaying = true
                this.syncRuntimeLipSyncState()
                this.statusText = this.trackInfo.visemes.length ? 'Avatar speaking' : 'Current audio has no viseme data'
            }
            const onPause = () => {
                this.isAudioPlaying = false
                this.syncRuntimeLipSyncState()
                if (!this.runtimeError) {
                    this.statusText = this.trackInfo.visemes.length ? 'Audio paused' : 'Current audio has no viseme data'
                }
            }
            const onEnded = () => {
                this.isAudioPlaying = false
                this.syncRuntimeLipSyncState()
                this.lastCueIndex = 0
                this.speechCueIndex = 0
                this.speechEnergy = 0
                this.blinkElapsedMs = -1
                this.blinkWaitMs = 1200
                this.blinkOpen = 1
                this.armUpperL = 0
                this.armUpperR = 0
                this.armLowerL = 0
                this.armLowerR = 0
                this.armUpperLTarget = 0
                this.armUpperRTarget = 0
                this.armLowerLTarget = 0
                this.armLowerRTarget = 0
                this.syncRuntimeLipSyncState()
                this.currentVisemeId = 0
                this.targetOpen = 0
                this.targetForm = 0
                this.syncRuntimeLipSyncState()
                this.statusText = this.trackInfo.visemes.length ? 'Audio playback completed' : 'Waiting for generated audio'
            }
            const onSeeking = () => {
                this.lastCueIndex = 0
                this.speechCueIndex = 0
                this.syncRuntimeLipSyncState()
            }
            element.addEventListener('play', onPlay)
            element.addEventListener('pause', onPause)
            element.addEventListener('ended', onEnded)
            element.addEventListener('seeking', onSeeking)
            this.removeAudioListeners = () => {
                element.removeEventListener('play', onPlay)
                element.removeEventListener('pause', onPause)
                element.removeEventListener('ended', onEnded)
                element.removeEventListener('seeking', onSeeking)
            }
            this.syncRuntimeLipSyncState()
        },
        detachAudioElement() {
            if (this.removeAudioListeners) {
                this.removeAudioListeners()
                this.removeAudioListeners = null
            }
            this.audioElement = null
            this.isAudioPlaying = false
            this.syncRuntimeLipSyncState()
        },
        setTrack(track) {
            const next = track && typeof track === 'object' ? track : emptyTrack()
            const visemes = Array.isArray(next.visemes)
                ? next.visemes.slice().sort((a, b) => a.timeMs - b.timeMs)
                : []
            this.trackInfo = {
                key: next.key || '',
                text: next.text || '',
                visemes,
                audioDurationMs: Math.max(0, Number(next.audioDurationMs) || 0),
                visemeCount: visemes.length
            }
            this.lastCueIndex = 0
            this.speechCues = this.buildSpeechCues(next.text || '', next.audioDurationMs)
            this.speechCueIndex = 0
            this.speechAccent = 0
            this.speechHeadLift = 0
            this.speechBodyTilt = 0
            this.speechFormBias = 0
            this.speechGazeBiasX = 0
            this.speechEnergy = 0
            this.blinkElapsedMs = -1
            this.blinkWaitMs = 900
            this.blinkOpen = 1
            this.armLeadSide = 1
            this.armUpperL = 0
            this.armUpperR = 0
            this.armLowerL = 0
            this.armLowerR = 0
            this.armUpperLTarget = 0
            this.armUpperRTarget = 0
            this.armLowerLTarget = 0
            this.armLowerRTarget = 0
            this.speechMood = this.detectSpeechMood(next.text || '')
            this.currentVisemeId = 0
            this.targetOpen = 0
            this.targetForm = 0
            this.syncRuntimeLipSyncState()
            this.statusText = visemes.length ? 'Visemes ready, waiting for audio playback' : 'Current audio has no viseme data'
        },
        clearTrack(statusText) {
            this.setTrack(emptyTrack())
            this.statusText = statusText || '绛夊緟鐢熸垚闊抽'
        },
        buildSpeechCues(text, audioDurationMs) {
            const source = String(text || '')
            const durationMs = Math.max(Number(audioDurationMs) || 0, 0)
            if (!source || !durationMs) return []
            const chars = Array.from(source)
            const speakableCount = chars.filter(char => /\S/.test(char)).length || chars.length || 1
            let seenSpeakable = 0
            const cues = []
            chars.forEach((char, index) => {
                const speakable = /\S/.test(char)
                if (speakable) seenSpeakable += 1
                const progress = seenSpeakable / speakableCount
                const timeMs = clamp(durationMs * progress, 0, durationMs)
                if (/[,\uFF0C\u3001;\uFF1B]/u.test(char)) {
                    cues.push({ timeMs, kind: 'pause' })
                } else if (/[?\uFF1F]/u.test(char)) {
                    cues.push({ timeMs, kind: 'question' })
                } else if (/[!\uFF01]/u.test(char)) {
                    cues.push({ timeMs, kind: 'exclaim' })
                } else if (/[.\u3002\u2026~\uFF5E]/u.test(char)) {
                    cues.push({ timeMs, kind: 'settle' })
                } else if (speakable && index > 0 && seenSpeakable % 18 === 0) {
                    cues.push({ timeMs, kind: 'beat' })
                }
            })
            return cues.sort((a, b) => a.timeMs - b.timeMs)
        },
        detectSpeechMood(text) {
            const source = String(text || '')
            return {
                headLift: /[?\uFF1F]/u.test(source) ? 0.9 : /[!\uFF01]/u.test(source) ? 0.45 : 0,
                bodyTilt: /[!\uFF01]/u.test(source) ? 0.7 : 0.2,
                formBias: /[?\uFF1F]/u.test(source) ? 0.06 : /[!\uFF01]/u.test(source) ? 0.1 : 0.03
            }
        },
        triggerSpeechCue(cue) {
            switch (cue.kind) {
            case 'pause':
                this.speechAccent = Math.max(this.speechAccent, 0.24)
                this.speechHeadLift = Math.max(this.speechHeadLift, 0.12)
                this.speechBodyTilt = Math.max(this.speechBodyTilt, 0.16)
                this.speechFormBias = Math.max(this.speechFormBias, 0.04)
                break
            case 'question':
                this.speechAccent = Math.max(this.speechAccent, 0.42)
                this.speechHeadLift = Math.max(this.speechHeadLift, 0.42)
                this.speechBodyTilt = Math.max(this.speechBodyTilt, 0.24)
                this.speechFormBias = Math.max(this.speechFormBias, 0.1)
                this.speechGazeBiasX = 0.08
                this.triggerArmGesture('question')
                break
            case 'exclaim':
                this.speechAccent = Math.max(this.speechAccent, 0.58)
                this.speechHeadLift = Math.max(this.speechHeadLift, 0.26)
                this.speechBodyTilt = Math.max(this.speechBodyTilt, 0.36)
                this.speechFormBias = Math.max(this.speechFormBias, 0.14)
                this.triggerArmGesture('exclaim')
                break
            case 'settle':
                this.speechAccent = Math.max(this.speechAccent, 0.2)
                this.speechHeadLift = Math.max(this.speechHeadLift, 0.08)
                this.speechBodyTilt = Math.max(this.speechBodyTilt, 0.1)
                break
            default:
                this.speechAccent = Math.max(this.speechAccent, 0.34)
                this.speechHeadLift = Math.max(this.speechHeadLift, 0.16)
                this.speechBodyTilt = Math.max(this.speechBodyTilt, 0.18)
                this.speechFormBias = Math.max(this.speechFormBias, 0.05)
                if (this.speechCueIndex % 2 === 0) {
                    this.triggerArmGesture('beat')
                }
                break
            }
        },
        triggerArmGesture(kind) {
            const leadIsLeft = this.armLeadSide > 0
            this.armLeadSide *= -1
            let leadUpper = 0
            let leadLower = 0
            let supportUpper = 0
            let supportLower = 0
            switch (kind) {
            case 'question':
                leadUpper = 3.2
                leadLower = 2
                supportUpper = -1
                supportLower = 0.7
                break
            case 'exclaim':
                leadUpper = 4.2
                leadLower = 2.4
                supportUpper = -1.4
                supportLower = 0.9
                break
            case 'pause':
                leadUpper = 1
                leadLower = 0.5
                supportUpper = -0.35
                supportLower = 0.2
                break
            case 'settle':
                leadUpper = -0.8
                leadLower = -0.4
                supportUpper = 0.3
                supportLower = -0.15
                break
            default:
                leadUpper = 2
                leadLower = 1.2
                supportUpper = -0.7
                supportLower = 0.4
                break
            }
            if (leadIsLeft) {
                this.armUpperLTarget = leadUpper
                this.armLowerLTarget = leadLower
                this.armUpperRTarget = supportUpper
                this.armLowerRTarget = supportLower
            } else {
                this.armUpperRTarget = -leadUpper
                this.armLowerRTarget = -leadLower
                this.armUpperLTarget = -supportUpper
                this.armLowerLTarget = -supportLower
            }
        },
        updateSpeechDynamics(delta) {
            const currentMs = this.audioElement ? Math.max(0, (this.audioElement.currentTime || 0) * 1000) : 0
            while (
                this.isAudioPlaying &&
                this.speechCueIndex < this.speechCues.length &&
                this.speechCues[this.speechCueIndex].timeMs <= currentMs
            ) {
                this.triggerSpeechCue(this.speechCues[this.speechCueIndex])
                this.speechCueIndex += 1
            }
            const decay = clamp(delta * 0.06, 0.08, 0.22)
            this.speechAccent = lerp(this.speechAccent, 0, decay)
            this.speechHeadLift = lerp(this.speechHeadLift, 0, decay * 0.9)
            this.speechBodyTilt = lerp(this.speechBodyTilt, 0, decay * 0.85)
            this.speechFormBias = lerp(this.speechFormBias, 0, decay * 0.8)
            this.speechGazeBiasX = lerp(this.speechGazeBiasX, 0, decay * 0.7)
        },
        updateArmGesture(delta) {
            const follow = clamp(delta * 0.12, 0.08, 0.18)
            const relax = clamp(delta * 0.16, 0.1, 0.24)
            this.armUpperL = lerp(this.armUpperL, this.armUpperLTarget, follow)
            this.armUpperR = lerp(this.armUpperR, this.armUpperRTarget, follow)
            this.armLowerL = lerp(this.armLowerL, this.armLowerLTarget, follow)
            this.armLowerR = lerp(this.armLowerR, this.armLowerRTarget, follow)
            this.armUpperLTarget = lerp(this.armUpperLTarget, 0, relax)
            this.armUpperRTarget = lerp(this.armUpperRTarget, 0, relax)
            this.armLowerLTarget = lerp(this.armLowerLTarget, 0, relax)
            this.armLowerRTarget = lerp(this.armLowerRTarget, 0, relax)
        },
        updateSpeechEnergy(delta) {
            const rise = clamp(delta * 0.22, 0.12, 0.34)
            const fall = clamp(delta * 0.12, 0.07, 0.18)
            const targetEnergy = this.isAudioPlaying
                ? clamp(
                    this.targetOpen * 0.62 +
                    this.currentOpen * 0.28 +
                    Math.abs(this.targetOpen - this.currentOpen) * 0.95 +
                    this.speechAccent * 0.38,
                    0,
                    1
                )
                : 0
            this.speechEnergy = lerp(
                this.speechEnergy,
                targetEnergy,
                targetEnergy >= this.speechEnergy ? rise : fall
            )
        },
        updateBlink(delta) {
            const deltaMs = delta * 16.6667
            if (this.blinkElapsedMs >= 0) {
                this.blinkElapsedMs += deltaMs
                const closeMs = 90
                const holdMs = 50
                const openMs = 130
                const totalMs = closeMs + holdMs + openMs
                if (this.blinkElapsedMs < closeMs) {
                    this.blinkOpen = 1 - this.blinkElapsedMs / closeMs
                    return
                }
                if (this.blinkElapsedMs < closeMs + holdMs) {
                    this.blinkOpen = 0
                    return
                }
                if (this.blinkElapsedMs < totalMs) {
                    const progress = (this.blinkElapsedMs - closeMs - holdMs) / openMs
                    this.blinkOpen = progress
                    return
                }
                this.blinkElapsedMs = -1
                this.blinkOpen = 1
                this.blinkWaitMs = 1700 + ((Math.sin(this.idleClockMs / 731) + 1) * 0.5) * 2200
                return
            }
            this.blinkWaitMs -= deltaMs
            if (this.blinkWaitMs <= 0) {
                this.blinkElapsedMs = 0
                this.blinkOpen = 1
            }
        },
        handleTicker(delta) {
            const factor = clamp(0.16 * delta, 0.08, 0.42)
            this.idleClockMs += delta * 16.6667
            this.updateSpeechDynamics(delta)
            this.updateArmGesture(delta)
            this.updateBlink(delta)
            this.updateMouthTargets()
            this.currentOpen = lerp(this.currentOpen, this.targetOpen, factor)
            this.currentForm = lerp(this.currentForm, this.targetForm, factor * 0.7)
            this.updateSpeechEnergy(delta)
            this.applyGestureParams(true)
            this.applyMouthParams(true)
            this.syncRuntimeLipSyncState()
        },
        updateMouthTargets() {
            if (!this.audioElement || !this.trackInfo.visemes.length || !this.isAudioPlaying) {
                this.targetOpen = 0
                this.targetForm = 0
                this.syncRuntimeLipSyncState()
                this.currentVisemeId = 0
                return
            }
            const timeline = this.trackInfo.visemes
            const currentMs = Math.max(0, (this.audioElement.currentTime || 0) * 1000 + 36)
            let index = this.lastCueIndex
            if (!timeline[index] || currentMs < timeline[index].timeMs) index = 0
            while (index + 1 < timeline.length && timeline[index + 1].timeMs <= currentMs) index += 1
            this.lastCueIndex = index
            const cue = timeline[index] || { visemeId: 0 }
            const mouth = this.mapVisemeToMouth(cue.visemeId)
            this.currentVisemeId = cue.visemeId || 0
            this.targetOpen = mouth.open
            this.targetForm = clamp(
                mouth.form + this.speechFormBias + (this.speechMood && this.speechMood.formBias ? this.speechMood.formBias : 0),
                -1,
                1
            )
        },
        mapVisemeToMouth(visemeId) {
            switch (Number(visemeId) || 0) {
            case 0:
                return { open: 0, form: 0 }
            case 21:
            case 19:
            case 20:
                return { open: 0.08, form: 0 }
            case 2:
            case 3:
                return { open: 0.92, form: 0.04 }
            case 7:
            case 8:
            case 9:
            case 10:
                return { open: 0.56, form: 0.18 }
            case 6:
            case 13:
            case 15:
            case 16:
                return { open: 0.34, form: -0.1 }
            case 1:
            case 4:
            case 5:
            case 11:
            case 12:
            case 14:
            case 17:
            case 18:
                return { open: 0.42, form: 0 }
            default:
                return { open: 0.28, form: 0 }
            }
        },
        syncMouthConfig() {
            const coreModel = this.live2dModel &&
                this.live2dModel.internalModel &&
                this.live2dModel.internalModel.coreModel
            if (!coreModel || typeof coreModel.getParameterIndex !== 'function') return
            const mouthOpenId = ['ParamMouthOpenY', 'PARAM_MOUTH_OPEN_Y']
                .find(id => coreModel.getParameterIndex(id) >= 0)
            const mouthFormId = ['ParamMouthForm', 'PARAM_MOUTH_FORM']
                .find(id => coreModel.getParameterIndex(id) >= 0)
            const mouthOpenIndex = mouthOpenId ? coreModel.getParameterIndex(mouthOpenId) : -1
            const mouthFormIndex = mouthFormId ? coreModel.getParameterIndex(mouthFormId) : -1
            const rawEntries = this.getRawParameterEntries(coreModel)
            const rawMouthOpen = rawEntries.find(entry => /mouth.*open|parammouthopeny/i.test(entry.normalizedId))
            const rawMouthForm = rawEntries.find(entry => /mouth.*form|parammouthform/i.test(entry.normalizedId))
            this.mouthConfig = {
                coreModel,
                mouthOpenId: (rawMouthOpen && rawMouthOpen.id) || mouthOpenId || '',
                mouthFormId: (rawMouthForm && rawMouthForm.id) || mouthFormId || '',
                mouthOpenIndex: rawMouthOpen ? rawMouthOpen.index : (mouthOpenIndex >= 0 ? mouthOpenIndex : -1),
                mouthFormIndex: rawMouthForm ? rawMouthForm.index : (mouthFormIndex >= 0 ? mouthFormIndex : -1),
                mouthOpenBase: rawMouthOpen ? rawMouthOpen.value : (mouthOpenIndex >= 0 ? coreModel.getParameterValueByIndex(mouthOpenIndex) : 0),
                mouthOpenMin: rawMouthOpen ? rawMouthOpen.minimumValue : (mouthOpenIndex >= 0 ? coreModel.getParameterMinimumValue(mouthOpenIndex) : 0),
                mouthOpenMax: rawMouthOpen ? rawMouthOpen.maximumValue : (mouthOpenIndex >= 0 ? coreModel.getParameterMaximumValue(mouthOpenIndex) : 1),
                mouthFormBase: rawMouthForm ? rawMouthForm.value : (mouthFormIndex >= 0 ? coreModel.getParameterValueByIndex(mouthFormIndex) : 0),
                mouthFormMin: rawMouthForm ? rawMouthForm.minimumValue : (mouthFormIndex >= 0 ? coreModel.getParameterMinimumValue(mouthFormIndex) : -1),
                mouthFormMax: rawMouthForm ? rawMouthForm.maximumValue : (mouthFormIndex >= 0 ? coreModel.getParameterMaximumValue(mouthFormIndex) : 1),
                mouthFormDefault: rawMouthForm ? rawMouthForm.defaultValue : (mouthFormIndex >= 0 ? coreModel.getParameterDefaultValue(mouthFormIndex) : 0)
            }
            const motionManager = this.live2dModel &&
                this.live2dModel.internalModel &&
                this.live2dModel.internalModel.motionManager
            if (motionManager && this.mouthConfig.mouthOpenId) {
                motionManager.lipSyncIds = [this.mouthConfig.mouthOpenId]
            }
            this.syncRuntimeLipSyncState()
        },
        readParameterValue(parameterId, parameterIndex) {
            if (!this.mouthConfig || !this.mouthConfig.coreModel) return 0
            const coreModel = this.mouthConfig.coreModel
            if (parameterId && typeof coreModel.getParameterValueById === 'function') {
                const value = coreModel.getParameterValueById(parameterId)
                if (typeof value === 'number' && !Number.isNaN(value)) return value
            }
            if (parameterIndex >= 0 && typeof coreModel.getParameterValueByIndex === 'function') {
                const value = coreModel.getParameterValueByIndex(parameterIndex)
                if (typeof value === 'number' && !Number.isNaN(value)) return value
            }
            const rawModel = typeof coreModel.getModel === 'function' ? coreModel.getModel() : null
            const values = rawModel && rawModel.parameters && rawModel.parameters.values
            if (values && parameterIndex >= 0 && typeof values[parameterIndex] === 'number') {
                return values[parameterIndex]
            }
            return 0
        },
        normalizeParameterId(parameterId) {
            if (parameterId == null) return ''
            if (typeof parameterId === 'string') return parameterId
            if (typeof parameterId.id === 'string') return parameterId.id
            if (typeof parameterId.s === 'string') return parameterId.s
            return String(parameterId)
        },
        getRawParameterEntries(coreModel = this.mouthConfig && this.mouthConfig.coreModel) {
            if (!coreModel || typeof coreModel.getModel !== 'function') return []
            const rawModel = coreModel.getModel()
            const parameters = rawModel && rawModel.parameters
            if (!parameters) return []
            const ids = parameters.ids || parameters.parameterIds || []
            const values = parameters.values || []
            const minimumValues = parameters.minimumValues || []
            const maximumValues = parameters.maximumValues || []
            const defaultValues = parameters.defaultValues || []
            return Array.from(ids).map((id, index) => ({
                id: this.normalizeParameterId(id),
                normalizedId: this.normalizeParameterId(id).toLowerCase(),
                index,
                value: Number(values[index] || 0),
                minimumValue: Number(minimumValues[index] || 0),
                maximumValue: Number(typeof maximumValues[index] === 'number' ? maximumValues[index] : 1),
                defaultValue: Number(defaultValues[index] || 0)
            }))
        },
        findParameterDescriptor(rawEntries, patterns) {
            if (!Array.isArray(rawEntries) || !rawEntries.length) return null
            for (const pattern of patterns) {
                const matcher = pattern instanceof RegExp ? pattern : new RegExp(pattern, 'i')
                const match = rawEntries.find(entry => matcher.test(entry.normalizedId))
                if (match) return match
            }
            return null
        },
        syncGestureConfig() {
            const coreModel = this.live2dModel &&
                this.live2dModel.internalModel &&
                this.live2dModel.internalModel.coreModel
            if (!coreModel) return
            const rawEntries = this.getRawParameterEntries(coreModel)
            const angleX = this.findParameterDescriptor(rawEntries, [/^param_angle_x$/i, /^angle_x$/i])
            const angleY = this.findParameterDescriptor(rawEntries, [/^param_angle_y$/i, /^angle_y$/i])
            const angleZ = this.findParameterDescriptor(rawEntries, [/^param_angle_z$/i, /^angle_z$/i])
            const bodyAngleX = this.findParameterDescriptor(rawEntries, [/^param_body_angle_x$/i, /^body_angle_x$/i])
            const bodyAngleY = this.findParameterDescriptor(rawEntries, [/^param_body_angle_y$/i, /^body_angle_y$/i])
            const bodyAngleZ = this.findParameterDescriptor(rawEntries, [/^param_body_angle_z$/i, /^body_angle_z$/i])
            const breath = this.findParameterDescriptor(rawEntries, [/^param_breath$/i, /^breath$/i])
            const eyeBallX = this.findParameterDescriptor(rawEntries, [/^param_eye_ball_x$/i, /^eye_ball_x$/i])
            const eyeBallY = this.findParameterDescriptor(rawEntries, [/^param_eye_ball_y$/i, /^eye_ball_y$/i])
            const eyeLOpen = this.findParameterDescriptor(rawEntries, [/^param_eye_l_open$/i, /^eye_l_open$/i])
            const eyeROpen = this.findParameterDescriptor(rawEntries, [/^param_eye_r_open$/i, /^eye_r_open$/i])
            const armLA = this.findParameterDescriptor(rawEntries, [/^param_arm_l_a$/i, /^arm_l_a$/i])
            const armRA = this.findParameterDescriptor(rawEntries, [/^param_arm_r_a$/i, /^arm_r_a$/i])
            const armLB = this.findParameterDescriptor(rawEntries, [/^param_arm_l_b$/i, /^arm_l_b$/i])
            const armRB = this.findParameterDescriptor(rawEntries, [/^param_arm_r_b$/i, /^arm_r_b$/i])
            this.gestureConfig = {
                angleX,
                angleY,
                angleZ,
                bodyAngleX,
                bodyAngleY,
                bodyAngleZ,
                breath,
                eyeBallX,
                eyeBallY,
                eyeLOpen,
                eyeROpen,
                armLA,
                armRA,
                armLB,
                armRB
            }
        },
        applyParameterDescriptor(descriptor, value, weight = 1) {
            if (!descriptor) return
            const minimum = typeof descriptor.minimumValue === 'number' ? descriptor.minimumValue : -1
            const maximum = typeof descriptor.maximumValue === 'number' ? descriptor.maximumValue : 1
            this.writeParameterValue(
                descriptor.id,
                descriptor.index,
                clamp(value, minimum, maximum),
                weight
            )
        },
        applyGestureParams() {
            if (!this.gestureConfig) return
            const t = this.idleClockMs / 1000
            const speechStrength = this.speechEnergy
            const accent = this.speechAccent
            const moodHeadLift = this.speechMood ? this.speechMood.headLift : 0
            const moodBodyTilt = this.speechMood ? this.speechMood.bodyTilt : 0
            const idleSway = Math.sin(t * 0.9)
            const idleLift = Math.sin(t * 1.7 + 0.8)
            const speechBeat = Math.sin(t * 7.2 + 0.35)
            const speechRoll = Math.sin(t * 5.4 + 1.1)
            const gazeDriftX = Math.sin(t * 0.45 + 0.5)
            const gazeDriftY = Math.sin(t * 0.63 + 2.1)
            const blinkBlend = this.isAudioPlaying && speechStrength > 0.12
                ? clamp(this.blinkOpen + speechStrength * 0.18, 0, 1)
                : this.blinkOpen

            this.applyParameterDescriptor(
                this.gestureConfig.angleX,
                idleSway * 2.6 + speechStrength * (speechBeat * (4.8 + accent * 2.1) + 1.1)
            )
            this.applyParameterDescriptor(
                this.gestureConfig.angleY,
                idleLift * 1.8 +
                speechStrength * (-0.5 + Math.sin(t * 3.1) * 0.9 + this.speechHeadLift * 3.2 + moodHeadLift)
            )
            this.applyParameterDescriptor(
                this.gestureConfig.angleZ,
                Math.sin(t * 0.7 + 1.4) * 1.2 + speechStrength * speechRoll * (2.2 + accent * 1.3)
            )
            this.applyParameterDescriptor(
                this.gestureConfig.bodyAngleX,
                idleSway * 0.8 +
                speechStrength * (Math.sin(t * 3.2 + 0.25) * (1.4 + accent * 0.8) + 0.15 + this.speechBodyTilt * 1.1 + moodBodyTilt * 0.35)
            )
            this.applyParameterDescriptor(
                this.gestureConfig.bodyAngleY,
                Math.sin(t * 1.1 + 0.7) * 0.35 + speechStrength * (this.speechHeadLift * 0.8)
            )
            this.applyParameterDescriptor(
                this.gestureConfig.bodyAngleZ,
                Math.sin(t * 0.95 + 0.3) * 0.45 + speechStrength * (speechRoll * 0.9)
            )
            this.applyParameterDescriptor(
                this.gestureConfig.breath,
                0.48 + Math.sin(t * 2.2) * 0.16 + speechStrength * (0.11 + accent * 0.05)
            )
            this.applyParameterDescriptor(
                this.gestureConfig.eyeLOpen,
                blinkBlend
            )
            this.applyParameterDescriptor(
                this.gestureConfig.eyeROpen,
                blinkBlend
            )
            this.applyParameterDescriptor(
                this.gestureConfig.eyeBallX,
                gazeDriftX * 0.14 + speechStrength * (Math.sin(t * 1.6) * 0.04 + this.speechGazeBiasX * 0.8)
            )
            this.applyParameterDescriptor(
                this.gestureConfig.eyeBallY,
                gazeDriftY * 0.06 - speechStrength * (0.02 + this.speechHeadLift * 0.03)
            )
            this.applyParameterDescriptor(
                this.gestureConfig.armLA,
                this.armUpperL
            )
            this.applyParameterDescriptor(
                this.gestureConfig.armRA,
                this.armUpperR
            )
            this.applyParameterDescriptor(
                this.gestureConfig.armLB,
                this.armLowerL
            )
            this.applyParameterDescriptor(
                this.gestureConfig.armRB,
                this.armLowerR
            )
        },
        writeParameterValue(parameterId, parameterIndex, value, weight = 1) {
            if (!this.mouthConfig || !this.mouthConfig.coreModel) return
            const coreModel = this.mouthConfig.coreModel
            if (parameterId && typeof coreModel.setParameterValueById === 'function') {
                coreModel.setParameterValueById(parameterId, value, weight)
            }
            if (parameterIndex >= 0 && typeof coreModel.setParameterValueByIndex === 'function') {
                coreModel.setParameterValueByIndex(parameterIndex, value, weight)
            }
            const rawModel = typeof coreModel.getModel === 'function' ? coreModel.getModel() : null
            const values = rawModel && rawModel.parameters && rawModel.parameters.values
            if (values && parameterIndex >= 0 && typeof values[parameterIndex] === 'number') {
                values[parameterIndex] = value
            }
        },
        refreshDebugState() {
            const internalModel = this.live2dModel && this.live2dModel.internalModel
            const motionManager = internalModel && internalModel.motionManager
            const hasMouthParam = this.mouthConfig && this.mouthConfig.mouthOpenIndex >= 0
            const rawEntries = this.getRawParameterEntries()
            const rawMouthEntries = rawEntries.filter(entry => /mouth|lip/i.test(entry.normalizedId))
            let modelOpen = 0
            if (hasMouthParam && this.mouthConfig.coreModel) {
                modelOpen = Number(this.readParameterValue(
                    this.mouthConfig.mouthOpenId,
                    this.mouthConfig.mouthOpenIndex
                ) || 0)
            }
            const rawOpenEntry = rawMouthEntries.find(entry => entry.index === this.mouthConfig.mouthOpenIndex) ||
                rawMouthEntries.find(entry => /mouth.*open|parammouthopeny/i.test(entry.normalizedId))
            const lastActiveSummary = this.debugState.lastActiveSummary || ''
            const nextLastActiveSummary = this.isAudioPlaying && (
                Number(this.targetOpen || 0) > 0 ||
                Number(this.currentOpen || 0) > 0 ||
                Number(modelOpen || 0) > 0 ||
                Number((rawOpenEntry && rawOpenEntry.value) || 0) > 0 ||
                Number(this.currentVisemeId || 0) > 0
            )
                ? `viseme ${this.currentVisemeId} | target ${Number(this.targetOpen || 0).toFixed(3)} | current ${Number(this.currentOpen || 0).toFixed(3)} | model ${modelOpen.toFixed(3)} | raw ${Number((rawOpenEntry && rawOpenEntry.value) || 0).toFixed(3)} | param ${((this.mouthConfig && this.mouthConfig.mouthOpenId) || '-')}`
                : lastActiveSummary
            this.debugState = {
                targetOpen: Number(this.targetOpen || 0).toFixed(3),
                currentOpen: Number(this.currentOpen || 0).toFixed(3),
                modelOpen: modelOpen.toFixed(3),
                rawOpen: Number((rawOpenEntry && rawOpenEntry.value) || 0).toFixed(3),
                audioAttached: !!this.audioElement,
                audioPlaying: !!this.isAudioPlaying,
                lipSyncEnabled: !!(internalModel && internalModel.lipSync && motionManager && motionManager.currentAudio),
                mouthOpenId: (this.mouthConfig && this.mouthConfig.mouthOpenId) || ''
                ,
                rawCandidates: rawMouthEntries.slice(0, 6).map(entry => `${entry.id}:${entry.index}:${entry.value.toFixed(3)}`).join(', '),
                lastActiveSummary: nextLastActiveSummary
            }
        },
        applyMouthParams() {
            if (!this.mouthConfig || !this.mouthConfig.coreModel) return
            if (this.mouthConfig.mouthOpenIndex >= 0 && this.mouthConfig.mouthOpenId) {
                const openRange = this.mouthConfig.mouthOpenMax - this.mouthConfig.mouthOpenMin
                const openValue = clamp(
                    this.mouthConfig.mouthOpenBase + openRange * clamp(this.currentOpen, 0, 1) * 0.68,
                    this.mouthConfig.mouthOpenMin,
                    this.mouthConfig.mouthOpenMax
                )
                this.writeParameterValue(
                    this.mouthConfig.mouthOpenId,
                    this.mouthConfig.mouthOpenIndex,
                    openValue,
                    1
                )
            }
            if (this.mouthConfig.mouthFormIndex >= 0) {
                const formSpan = (this.mouthConfig.mouthFormMax - this.mouthConfig.mouthFormMin) * 0.22
                const formValue = clamp(
                    this.mouthConfig.mouthFormBase + this.currentForm * formSpan,
                    this.mouthConfig.mouthFormMin,
                    this.mouthConfig.mouthFormMax
                )
                this.writeParameterValue(
                    this.mouthConfig.mouthFormId,
                    this.mouthConfig.mouthFormIndex,
                    formValue,
                    1
                )
            }
            this.refreshDebugState()
        }
    }
}
</script>

<style scoped lang="less">
.live2d-panel {
    margin-top: 18px;
    padding: 18px 18px 16px;
    border-radius: 18px;
    border: 1px solid #e5e7eb;
    background: linear-gradient(180deg, #ffffff 0%, #f7f8fb 100%);
    box-shadow: 0 16px 30px rgba(15, 23, 42, 0.06);
}

.live2d-panel__header {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    align-items: flex-start;
    margin-bottom: 12px;
}

.live2d-panel__header h3 {
    margin: 0;
    font-size: 18px;
    color: #111827;
}

.live2d-panel__header p {
    margin: 6px 0 0;
    font-size: 13px;
    color: #6b7280;
}

.live2d-panel__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.live2d-panel__meta span {
    padding: 6px 10px;
    border-radius: 999px;
    background: rgba(37, 99, 235, 0.08);
    color: #1d4ed8;
    font-size: 12px;
    line-height: 1;
}

.live2d-stage-wrap {
    position: relative;
    min-height: 440px;
    border-radius: 16px;
    overflow: hidden;
    background:
        radial-gradient(circle at top, rgba(59, 130, 246, 0.14), transparent 44%),
        linear-gradient(180deg, #eef6ff 0%, #f8fbff 48%, #eef2f7 100%);
}

.live2d-stage {
    width: 100%;
    height: 440px;
}

.live2d-stage__overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    color: #475569;
    font-size: 14px;
    text-align: center;
    background: rgba(255, 255, 255, 0.65);
    backdrop-filter: blur(4px);
}

.live2d-stage__overlay--error {
    color: #b91c1c;
    background: rgba(255, 244, 244, 0.88);
}

.live2d-panel__footer {
    margin-top: 12px;
    display: grid;
    gap: 8px;
}

.live2d-status {
    font-size: 13px;
    color: #1f2937;
    font-weight: 500;
}

.live2d-note {
    font-size: 12px;
    color: #6b7280;
    line-height: 1.5;
}

.live2d-debug {
    font-size: 12px;
    color: #475569;
    line-height: 1.5;
    font-family: Consolas, 'Courier New', monospace;
    word-break: break-word;
}

@media (max-width: 768px) {
    .live2d-panel {
        padding: 14px;
        border-radius: 16px;
    }

    .live2d-panel__header {
        flex-direction: column;
        margin-bottom: 10px;
    }

    .live2d-stage-wrap,
    .live2d-stage {
        min-height: 360px;
        height: 360px;
    }
}
</style>

<style lang="less">
.dark-mode .live2d-panel {
    border-color: var(--border-color);
    background: linear-gradient(180deg, rgba(29, 39, 56, 0.98) 0%, rgba(22, 30, 44, 1) 100%);
    box-shadow: 0 16px 30px rgba(0, 0, 0, 0.26);
}

.dark-mode .live2d-panel__header h3,
.dark-mode .live2d-status {
    color: var(--text-primary);
}

.dark-mode .live2d-panel__header p,
.dark-mode .live2d-note,
.dark-mode .live2d-debug {
    color: var(--text-regular);
}

.dark-mode .live2d-panel__meta span {
    background: rgba(64, 158, 255, 0.16);
    color: #8cc8ff;
}

.dark-mode .live2d-stage-wrap {
    background:
        radial-gradient(circle at top, rgba(64, 158, 255, 0.2), transparent 42%),
        linear-gradient(180deg, #1c2736 0%, #16202e 48%, #121924 100%);
}

.dark-mode .live2d-stage__overlay {
    color: var(--text-regular);
    background: rgba(18, 25, 36, 0.72);
}

.dark-mode .live2d-stage__overlay--error {
    color: #ffb4b4;
    background: rgba(87, 28, 28, 0.78);
}
</style>
